import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { ar as arLocale, enUS } from 'date-fns/locale';

/** Merge Tailwind classes, last one wins on conflicts. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------------------
// Dates — NFR-10, one clock only: the clinic wall clock
// ---------------------------------------------------------------------------
//
// Timestamps cross the wire as naive wall clock, so nothing here converts. The
// API serialises Dates with a trailing `Z` it does not mean, and honouring it
// would re-introduce the browser's own offset — so the offset is stripped and
// the digits are read as-is. Whatever the clinic typed is what gets printed,
// on any machine, in any timezone.

/** Drop the offset the API tacks onto its timestamps — those digits are already local. */
function stripOffset(value: string): string {
  return value.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '');
}

function asDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const d = typeof value === 'string' ? parseISO(stripOffset(value)) : value;
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Identity — kept because ~200 call sites read as "the clinic's view of this
 * timestamp", and that is now simply what every timestamp is.
 */
export function toClinic(value: Date | string | null | undefined): Date | null {
  return asDate(value);
}

/**
 * The write direction: a Date to the naive wall-clock string the API stores.
 * Deliberately no `Z` and no offset — the digits are the whole message.
 */
export function toApiDateTime(value: Date | string | null | undefined): string | undefined {
  const d = asDate(value);
  return d ? format(d, "yyyy-MM-dd'T'HH:mm:ss.SSS") : undefined;
}

/** A `yyyy-MM-dd` picker value plus the current time-of-day, as an API timestamp. */
export function dateInputToApi(date: string | null | undefined): string | undefined {
  if (!date) return undefined;
  const now = new Date();
  const parsed = parseISO(`${date}T${format(now, 'HH:mm:ss.SSS')}`);
  return Number.isNaN(parsed.getTime()) ? undefined : format(parsed, "yyyy-MM-dd'T'HH:mm:ss.SSS");
}

/**
 * BIL-02 — the collection moment a payment dialog sends. The picker is seeded
 * with today when the dialog opens; left untouched, nothing is sent and the
 * server stamps the real moment (clinic clock) — a dialog opened before
 * midnight and saved after it must not record the previous day at the new
 * day's time. A day the cashier picks herself keeps `dateInputToApi`.
 */
export function collectionDateToApi(date: string | null | undefined, seeded: string): string | undefined {
  if (!date || date === seeded) return undefined;
  return dateInputToApi(date);
}

export function formatDate(value: Date | string | null | undefined, lang: 'AR' | 'EN' = 'AR'): string {
  const d = toClinic(value);
  return d ? format(d, 'yyyy/MM/dd', { locale: lang === 'AR' ? arLocale : enUS }) : '—';
}

export function formatTime(value: Date | string | null | undefined, lang: 'AR' | 'EN' = 'AR'): string {
  const d = toClinic(value);
  return d ? format(d, 'hh:mm a', { locale: lang === 'AR' ? arLocale : enUS }) : '—';
}

export function formatDateTime(value: Date | string | null | undefined, lang: 'AR' | 'EN' = 'AR'): string {
  const d = toClinic(value);
  return d ? format(d, 'yyyy/MM/dd hh:mm a', { locale: lang === 'AR' ? arLocale : enUS }) : '—';
}

/** "قبل ٢٠ دقيقة" — used by the waiting counters (DOC-03, APT-13). */
export function formatRelative(value: Date | string | null | undefined, lang: 'AR' | 'EN' = 'AR'): string {
  const d = asDate(value);
  return d ? formatDistanceToNowStrict(d, { addSuffix: true, locale: lang === 'AR' ? arLocale : enUS }) : '—';
}

/** Whole minutes elapsed since an instant — the wait-time counter ticks on this. */
export function minutesSince(value: Date | string | null | undefined): number {
  const d = asDate(value);
  if (!d) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
}

/** "HH:mm" for a time-of-day input, in clinic time. */
export function toTimeInput(value: Date | string | null | undefined): string {
  const d = toClinic(value);
  return d ? format(d, 'HH:mm') : '';
}

/** "yyyy-MM-dd" for a date input, in clinic time. */
export function toDateInput(value: Date | string | null | undefined): string {
  const d = toClinic(value);
  return d ? format(d, 'yyyy-MM-dd') : '';
}

export function minutesToLabel(minutes: number, lang: 'AR' | 'EN' = 'AR'): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (lang === 'EN') return h ? `${h}h ${m ? `${m}m` : ''}`.trim() : `${m}m`;
  if (h && m) return `${h} س ${m} د`;
  if (h) return `${h} ساعة`;
  return `${m} د`;
}

// ---------------------------------------------------------------------------
// Misc UI helpers
// ---------------------------------------------------------------------------

/** Two-letter avatar seed for provider and patient chips (CAL-14). */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '؟';
  return parts.length === 1 ? parts[0].charAt(0) : `${parts[0].charAt(0)}${parts[1].charAt(0)}`;
}

/** Tint a provider/tag hex colour for use as a soft background. */
export function withAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The same soft tint as `withAlpha`, pre-mixed with white and returned opaque.
 *
 * Use this wherever the tinted surface sits on top of something with its own
 * texture — the calendar grid's hour rules, a shaded out-of-hours band, a
 * highlighted month cell. `withAlpha` would let all of that read straight
 * through the card and make a booked slot look see-through; mixing against
 * white up front gives the identical colour on a white page with none of the
 * bleed-through.
 */
export function opaqueTint(hex: string, amount: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const mix = (offset: number) => Math.round(255 + (parseInt(clean.slice(offset, offset + 2), 16) - 255) * amount);
  return `rgb(${mix(0)}, ${mix(2)}, ${mix(4)})`;
}

/** Debounce a value — search boxes on dense list screens. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
